<template>
  <modal @save="submit" @back="goBack">
    <template #header>{{ $t('reviewSubmit.modal.title.editPersonalInfo') }}</template>
    <form autocomplete="off">
      <input-inline>
        <input-text
          id="first-name"
          v-model="primaryOwner.firstName"
          name="first-name"
          :maxlength="FIRST_NAME_MAX_LENGTH"
          v-validation="[['dynamic', '/owners/*/firstName']]"
        >
          <template #label>{{ $t('basicInfo.firstNameLabel') }}</template>
        </input-text>

        <input-text
          id="last-name"
          v-model="primaryOwner.lastName"
          name="last-name"
          :maxlength="LAST_NAME_MAX_LENGTH"
          :strict-mode="{ regex: /\./g }"
          v-validation="[['dynamic', '/owners/*/lastName']]"
        >
          <template #label>{{ $t('basicInfo.lastNameLabel') }}</template>
        </input-text>
      </input-inline>

      <input-text
        id="email"
        v-model="primaryOwner.email"
        name="email"
        :maxlength="EMAIL_MAX_LENGTH"
        v-validation="[['dynamic', '/owners/*/email']]"
        :strictMode="{ skipDefault: true, regex: 'email' }"
        :disabled="true"
      >
        <template #label>{{ $t('basicInfo.email.label') }}</template>
      </input-text>

      <address-autocomplete
        id="applicant-address"
        labelPathReference="ownerInfo"
        v-model="primaryOwner"
        validationPathPrefix="/owners/*"
        :maxlength="ADDRESS_MAX_LENGTH"
        :postalCodePlaceholder="$t('postalCodePlaceholder')"
        :placeholder="'ownerInfo.addressPlaceholder'"
        :stateDefaultValue="$t('select')"
        @autocomplete="onPlaceChange"
      >
        <template #phone>
          <input-text-masked
            id="primary-phone"
            ref="primaryPhone"
            class="primaryPhone"
            name="primary-phone"
            v-model="primaryOwner.primaryPhone"
            :maxlength="PHONE_MAX_LENGTH"
            v-validation.ifPresent="[['dynamic', '/owners/*/primaryPhone']]"
            :mask="primaryPhoneMask"
            :cleanup-pattern="/[-|\s|(|)]/g"
            :placeholder-char="'\u2000'"
            :guide="false"
          >
            <template #label>{{ $t('basicInfo.bestContactNumber.label') }}</template>
          </input-text-masked>
        </template>
      </address-autocomplete>

      <input-inline>
        <input-age
          id="date-of-birth"
          ref="dateOfBirth"
          v-model="primaryOwner.dateOfBirth"
          v-validation="[['dynamic', '/owners/*/dateOfBirth']]"
        >
          <template #label>{{ $t('ownerInfo.dateOfBirthLabel') }}</template>
        </input-age>

        <input-select
          id="residency-status"
          name="residency-status"
          v-model="primaryOwner._extension.residencyStatus"
          :options="residencyStatusOptions"
          v-validation:closed="[['dynamic', '/owners/*/_extension/residencyStatus']]"
          :defaultValue="$t('select')"
        >
          <template #label>{{ $t('ownerInfo.residencyStatus.label') }}</template>
        </input-select>
      </input-inline>

      <input-inline>
        <input-select
          id="how-did-you-hear-about-us"
          name="how-did-you-hear-about-us"
          v-model="application.hearAboutUs"
          :options="howDidYouHearAboutUsOptions"
          v-validation:closed="[['dynamic', '/hearAboutUs']]"
          :defaultValue="$t('select')"
        >
          <template #label>{{ $t('ownerInfo.howDidYouHearAboutUs.label') }}</template>
        </input-select>
        <input-text
          type="text"
          id="unique-loan-code"
          name="unique-loan-code"
          :maxlength="UNIQUE_LOAN_CODE_LENGTH"
          v-model="application._extension.uniqueLoanCode"
          v-validation="[['dynamic', '/_extension/uniqueLoanCode']]"
        >
          <template #label>{{ $t('ownerInfo.uniqueLoanCodeLabel') }}</template>
          <template #description>{{ $t('ownerInfo.uniqueLoanCodeLabelSuffix') }}</template>
        </input-text>
      </input-inline>

      <input-inline>
        <input-text-masked
          id="additional-phone"
          class="additionalPhone"
          name="additional-phone"
          v-model="primaryOwner.additionalPhone"
          :maxlength="PHONE_MAX_LENGTH"
          v-validation.ifPresent="[['dynamic', '/owners/*/additionalPhone']]"
          :mask="additionalPhoneMask"
          :cleanup-pattern="/[-|\s|(|)]/g"
          :placeholder-char="'\u2000'"
          :guide="false"
        >
          <template #label>{{ $t('ownerInfo.additionalPhoneField.label') }}</template>
          <template #description>{{ $t('ownerInfo.additionalPhoneField.suffix') }}</template>
        </input-text-masked>
      </input-inline>
    </form>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import InputInline from '@/components/InputInline';
import InputAge from '@/components/InputAge';
import modalMixin from '@/mixins/modal';
import {
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  UNIQUE_LOAN_CODE_LENGTH,
} from '@/modules/validation-service/fields-length';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import residencyStatusTypes from '@/modules/residency-status-types';
import informationSourceTypes from '@/modules/information-source-types';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import states from '@/modules/states';
import { getPhoneMask } from '@/modules/field-mask';

export default {
  name: 'edit-applicant-personal-info',
  components: {
    Modal,
    InputInline,
    InputAge,
    AddressAutocomplete,
  },
  data() {
    return {
      FIRST_NAME_MAX_LENGTH,
      LAST_NAME_MAX_LENGTH,
      EMAIL_MAX_LENGTH,
      ADDRESS_MAX_LENGTH,
      CITY_MAX_LENGTH,
      POSTAL_CODE_MAX_LENGTH,
      PHONE_MAX_LENGTH,
      UNIQUE_LOAN_CODE_LENGTH,
    };
  },
  computed: {
    primaryOwner: {
      get() {
        return this.application.owners[0];
      },
      set(value) {
        this.application = {
          ...this.application,
          owners: [value, ...this.application.owners.slice(1)],
        };
      },
    },
    states() {
      return mapConstsToOptions(states, 'states');
    },
    residencyStatusOptions() {
      return mapConstsToOptions(residencyStatusTypes, 'ownerInfo.residencyStatus.options');
    },
    howDidYouHearAboutUsOptions() {
      return mapConstsToOptions(informationSourceTypes, 'ownerInfo.howDidYouHearAboutUs.options');
    },
    primaryPhoneMask() {
      return getPhoneMask(this.primaryOwner.primaryPhone);
    },
    additionalPhoneMask() {
      return getPhoneMask(this.primaryOwner.additionalPhone);
    },
  },
  methods: {
    setFocusTo(element) {
      if (element) {
        this.$nextTick().then(() => {
          element.focus();
        });
      }
    },
    onPlaceChange() {
      this.setFocusTo(this.$refs.primaryPhone.$refs.maskedInput.$refs.input);
    },
  },
  mixins: [modalMixin],
};
</script>

<style lang="sass" scoped></style>
